/**
 * 통합 애널리틱스 레이어.
 *
 * PostHog · Mixpanel · GA4(Firebase) 세 SDK를 하나의 인터페이스로 추상화한다.
 * - `initAnalytics()`: 앱 시작 시 1회 호출
 * - `identifyUser()` / `resetAnalytics()`: 로그인·로그아웃 시 유저 식별
 * - `trackEvent()`: 커스텀 이벤트
 * - `trackPageView()`: 페이지 전환
 */

import type { Analytics as FirebaseAnalytics } from "firebase/analytics";

/* ---------- 내부 상태 ---------- */

const isDev = import.meta.env.DEV;

let posthogLoaded = false;
let mixpanelLoaded = false;
let firebaseAnalytics: FirebaseAnalytics | null = null;

/* ---------- 초기화 ---------- */

export async function initAnalytics() {
  if (isDev) {
    return;
  }

  // PostHog
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST;

  if (posthogKey) {
    const posthog = (await import("posthog-js")).default;

    posthog.init(posthogKey, {
      api_host: posthogHost || "https://us.i.posthog.com",
      capture_pageview: false, // 수동으로 trackPageView 사용
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      loaded: () => {
        posthogLoaded = true;
      },
    });
  }

  // Mixpanel
  const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN;

  if (mixpanelToken) {
    const mixpanel = (await import("mixpanel-browser")).default;

    mixpanel.init(mixpanelToken, {
      track_pageview: false, // 수동 제어
      persistence: "localStorage",
    });

    mixpanelLoaded = true;
  }

  // GA4 (Firebase Analytics)
  const firebaseApiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const firebaseMeasurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

  if (firebaseApiKey && firebaseMeasurementId) {
    const { initializeApp } = await import("firebase/app");
    const { getAnalytics, isSupported } = await import("firebase/analytics");

    const supported = await isSupported();

    if (supported) {
      const app = initializeApp({
        apiKey: firebaseApiKey,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
        measurementId: firebaseMeasurementId,
      });

      firebaseAnalytics = getAnalytics(app);
    }
  }

  // Vexo (프로덕션 전용, index.html에서 제거됨)
  const script = document.createElement("script");
  script.src = "https://www.vexo.co/analytics.js";
  script.defer = true;
  script.onerror = () => {
    // 네트워크/차단으로 로드 실패해도 앱 동작에 영향 없음 (무시)
  };
  document.head.appendChild(script);
}

/* ---------- 유저 식별 ---------- */

export async function identifyUser(userId: string, properties?: Record<string, string>) {
  if (isDev) {
    return;
  }

  if (posthogLoaded) {
    const posthog = (await import("posthog-js")).default;
    posthog.identify(userId, properties);
  }

  if (mixpanelLoaded) {
    const mixpanel = (await import("mixpanel-browser")).default;
    mixpanel.identify(userId);

    if (properties) {
      mixpanel.people.set(properties);
    }
  }

  if (firebaseAnalytics) {
    const { setUserId, setUserProperties } = await import("firebase/analytics");
    setUserId(firebaseAnalytics, userId);

    if (properties) {
      setUserProperties(firebaseAnalytics, properties);
    }
  }

  (globalThis as any).vexo?.identifyDevice(userId);
}

/** 로그아웃 시 모든 SDK의 유저 컨텍스트를 초기화한다. */
export async function resetAnalytics() {
  if (isDev) {
    return;
  }

  if (posthogLoaded) {
    const posthog = (await import("posthog-js")).default;
    posthog.reset();
  }

  if (mixpanelLoaded) {
    const mixpanel = (await import("mixpanel-browser")).default;
    mixpanel.reset();
  }

  if (firebaseAnalytics) {
    const { setUserId } = await import("firebase/analytics");
    setUserId(firebaseAnalytics, null);
  }
}

/* ---------- 이벤트 ---------- */

export async function trackEvent(name: string, properties?: Record<string, unknown>) {
  if (isDev) {
    return;
  }

  if (posthogLoaded) {
    const posthog = (await import("posthog-js")).default;
    posthog.capture(name, properties);
  }

  if (mixpanelLoaded) {
    const mixpanel = (await import("mixpanel-browser")).default;
    mixpanel.track(name, properties);
  }

  if (firebaseAnalytics) {
    const { logEvent } = await import("firebase/analytics");
    logEvent(firebaseAnalytics, name, properties);
  }
}

/* ---------- 네이티브 브릿지 이벤트 수신 ---------- */

/**
 * 네이티브 앱(Expo WebView)에서 `sendMessageToWebView('TRACK_EVENT', { name, properties })`로
 * 전달하는 이벤트를 수신하여 동일한 `trackEvent`로 전송한다.
 * 별도 SDK 설치 없이 웹·네이티브 이벤트를 하나의 유저 프로필로 통합.
 */
export function setupNativeEventBridge() {
  function handleNativeMessage(event: MessageEvent) {
    if (!event.data) {
      return;
    }

    try {
      const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      if (data.type === "TRACK_EVENT" && data.payload?.name) {
        trackEvent(data.payload.name, data.payload.properties);
      }
    } catch {
      // 파싱 실패 무시 (다른 메시지일 수 있음)
    }
  }

  // WebView postMessage는 document 또는 window 이벤트로 전달됨
  document.addEventListener("message", handleNativeMessage as EventListener);
  window.addEventListener("message", handleNativeMessage);
}

/* ---------- 페이지뷰 ---------- */

export async function trackPageView(path: string) {
  if (isDev) {
    return;
  }

  if (posthogLoaded) {
    const posthog = (await import("posthog-js")).default;
    posthog.capture("$pageview", { $current_url: path });
  }

  if (mixpanelLoaded) {
    const mixpanel = (await import("mixpanel-browser")).default;
    mixpanel.track_pageview({ page: path });
  }

  if (firebaseAnalytics) {
    const { logEvent } = await import("firebase/analytics");
    logEvent(firebaseAnalytics, "page_view", { page_path: path });
  }
}
